import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isDriverAuthenticated } from '../utils/driverAuthUtils';
import { clearDriverAuth } from '../features/driver/driverAuthSlice';
import { verifyDriverToken } from '../services/driverAuthService';

/**
 * Component to monitor driver authentication status and handle session expiry
 * This component runs in the background and periodically verifies the driver session
 */
const DriverAuthMonitor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const driverAuthState = useSelector((state) => state.driverAuth);

  useEffect(() => {
    // TEMPORARILY DISABLED - Causing logout loops
    // TODO: Re-enable once backend authentication issues are resolved
    return;

    // Only monitor on driver pages
    if (!location.pathname.startsWith('/driver/')) {
      return;
    }

    // Skip monitoring on login/register pages
    if (location.pathname === '/driver/login' || location.pathname === '/driver/register') {
      return;
    }

    const monitorAuthentication = async () => {
      try {
        // First check local authentication
        if (!isDriverAuthenticated(driverAuthState)) {
          handleAuthFailure();
          return;
        }

        // Only verify with server if we have a token and it's been more than 10 minutes
        const lastVerification = localStorage.getItem('lastDriverTokenVerification');
        const now = Date.now();
        const shouldVerifyServer = !lastVerification || (now - parseInt(lastVerification)) > 10 * 60 * 1000;

        if (shouldVerifyServer && driverAuthState.token) {
          const isServerValid = await verifyDriverToken(driverAuthState);
          if (!isServerValid) {
            handleAuthFailure();
            return;
          }
          localStorage.setItem('lastDriverTokenVerification', now.toString());
        }

        // Authentication is valid
      } catch (error) {
        console.error('DriverAuthMonitor: Error during authentication check:', error);
        // On error, don't immediately logout - might be network issue
        // Only logout if it's been over 30 minutes since last successful verification
        const lastVerification = localStorage.getItem('lastDriverTokenVerification');
        if (!lastVerification || (Date.now() - parseInt(lastVerification)) > 30 * 60 * 1000) {
          handleAuthFailure();
        }
      }
    };

    const handleAuthFailure = () => {
      // Clear auth state
      dispatch(clearDriverAuth());
      
      // Show notification
      toast.error('Your session has expired. Please login again.', {
        toastId: 'driver-session-expired'
      });
      
      // Redirect to login
      navigate('/driver/login', { 
        replace: true, 
        state: { from: location.pathname } 
      });
    };

    // Initial check with delay to avoid interfering with login flow
    const initialCheck = setTimeout(monitorAuthentication, 2000);

    // Set up periodic checks every 10 minutes (less frequent)
    const authCheckInterval = setInterval(monitorAuthentication, 10 * 60 * 1000);

    // Cleanup on unmount
    return () => {
      clearTimeout(initialCheck);
      clearInterval(authCheckInterval);
    };
  }, [driverAuthState, location.pathname, dispatch, navigate]);

  // This component doesn't render anything
  return null;
};

export default DriverAuthMonitor;
